import { useState } from 'react';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Button, Form, Input, message } from 'antd';
import { LangText } from '@/components';
import { apiBuyerModify } from '@/service/api';
import '@/components/AuthModal/index.scss';

const EditPasswordForm = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const res = await apiBuyerModify(values);
      if (res.code === 0) {
        message.success(t('profile.message.passwordSaveSuccess'));
        onClose();
      } else {
        message.error(res.msg || t('profile.message.saveFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const passwordIconRender = (visible: boolean) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />);

  return (
    <div className="px-[8px] pb-[48px] pt-[32px]">
      <LangText lngKey="profile.passwordModal.title" className="block text-[32px] font-bold leading-[45px] text-[#000]" />
      <Form form={form} layout="vertical" className="!mt-[40px]" onFinish={handleSubmit}>
        <Form.Item
          name="originPassword"
          required
          label={<LangText lngKey="profile.passwordModal.originPassword" className="text-[24px] font-bold text-[#000]" />}
          rules={[{ required: true, message: t('profile.validation.originPasswordRequired') }]}
        >
          <Input.Password
            placeholder={t('profile.placeholder.password')}
            className="auth-login-password"
            autoComplete="current-password"
            disabled={loading}
            iconRender={passwordIconRender}
          />
        </Form.Item>

        <Form.Item
          name="newPassword"
          required
          label={<LangText lngKey="profile.passwordModal.newPassword" className="text-[24px] font-bold text-[#000]" />}
          rules={[
            { required: true, message: t('auth.validation.passwordRequired') },
            { min: 6, message: t('auth.validation.passwordLength') },
            { max: 12, message: t('auth.validation.passwordLength') },
            { pattern: /^[a-zA-Z0-9]+$/, message: t('profile.validation.passwordLetterNumber') },
          ]}
        >
          <Input.Password
            placeholder={t('profile.placeholder.password')}
            className="auth-login-password"
            autoComplete="new-password"
            disabled={loading}
            iconRender={passwordIconRender}
          />
        </Form.Item>

        <Form.Item
          name="newConfirmPassword"
          required
          label={<LangText lngKey="profile.passwordModal.confirmPassword" className="text-[24px] font-bold text-[#000]" />}
          dependencies={['newPassword']}
          rules={[
            { required: true, message: t('auth.validation.confirmPasswordRequired') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('auth.validation.passwordMismatch')));
              },
            }),
          ]}
        >
          <Input.Password
            placeholder={t('profile.placeholder.confirmPassword')}
            className="auth-login-password"
            autoComplete="new-password"
            disabled={loading}
            iconRender={passwordIconRender}
          />
        </Form.Item>

        <div className="mt-[52px] flex items-center gap-4">
          <Button
            className="!h-[66px] !w-[180px] !rounded-full !border !border-[#D9D9D9] !bg-white !text-[24px] !text-[#666] hover:!border-[#D9D9D9] hover:!bg-white hover:!text-[#666]"
            onClick={onClose}
            disabled={loading}
          >
            <LangText lngKey="common.action.cancel" />
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            className="!h-[66px] flex-1 !rounded-full !bg-primary !text-[24px]"
          >
            <LangText lngKey="common.action.save" />
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EditPasswordForm;
